import { Component, ComponentFactoryResolver, ComponentRef, OnDestroy, OnInit, SimpleChange, ViewChild, ViewContainerRef } from '@angular/core';
import { GetSituFacadeResponse, SituViewWebApiClient, GetSituFacadeRequest, ProductName, GetHomeRequest, GetHomeResponse, RenderedOutputStatus } from 'situview-webapi-client';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SituFacadeReadonlyComponent } from '../situ-facade-readonly/situ-facade-readonly.component';
import { SituViewReadonlyComponent } from '../situ-view-readonly/situ-view-readonly.component';
import { SituProductReadonlyComponent } from '../situ-product-readonly/situ-product-readonly.component';
import { SituPlanReadonlyComponent } from '../situ-plan-readonly/situ-plan-readonly.component';
import { Guid } from 'guid-typescript';
import { SituRenderReadonlyComponent } from '../situ-render-readonly/situ-render-readonly.component';
import { SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import { SituReel, SituReelItem, SituReelService } from 'src/app/services/situ-reel.service';
import { isDaydreamProduct } from 'src/app/shared/models/daydream-product';

@Component({
  selector: 'situ-readonly-factory',
  template: `
    <ng-container #dynamicComponentContainer></ng-container>
    <div class="situ-transition-overlay" *ngIf="isTransitioning">
      <div class="situ-transition-stage">
        <div class="situ-transition-glow"></div>
        <img src="assets/images/logo-icon.png" alt="situ" class="situ-transition-logo">
      </div>
    </div>
    <situ-reel-strip
      *ngIf="activeReel"
      [reel]="activeReel"
      [selectedArtifactId]="selectedArtifactId"
      (itemSelected)="onItemSelected($event)">
    </situ-reel-strip>
  `,
  styles: [`
    .situ-transition-overlay {
      position: fixed;
      inset: 0;
      z-index: 40;
      background:
        radial-gradient(circle at center, rgba(235, 20, 92, 0.10) 0%, rgba(235, 20, 92, 0) 35%),
        radial-gradient(circle at center, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.72) 80%);
      backdrop-filter: blur(18px) saturate(120%);
      -webkit-backdrop-filter: blur(18px) saturate(120%);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: situ-overlay-fade 280ms ease-out;
      pointer-events: all;
    }
    .situ-transition-stage {
      position: relative;
      width: 140px;
      height: 140px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .situ-transition-glow {
      position: absolute;
      inset: -60px;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        rgba(235, 20, 92, 0.55) 0%,
        rgba(235, 20, 92, 0.18) 35%,
        transparent 70%
      );
      filter: blur(14px);
      animation: situ-glow-pulse 2.4s ease-in-out infinite;
    }
    .situ-transition-logo {
      position: relative;
      width: 64px;
      height: 64px;
      animation: situ-breathe 2.4s ease-in-out infinite;
    }
    @keyframes situ-overlay-fade {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes situ-breathe {
      0%, 100% { opacity: 0.78; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.06); }
    }
    @keyframes situ-glow-pulse {
      0%, 100% { opacity: 0.6; transform: scale(0.95); }
      50% { opacity: 1; transform: scale(1.1); }
    }
  `]
})
export class SituReadonlyFactoryComponent implements OnInit, OnDestroy {
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef, static: true })
  private dynamicContainer!: ViewContainerRef;

  activeReel: SituReel | null = null;
  selectedArtifactId: string | undefined;
  isTransitioning = false;

  private currentComponentRef: ComponentRef<any> | null = null;
  private paramSubscription: Subscription | undefined;
  private transitionHideTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private componentFactoryResolver: ComponentFactoryResolver,
    private viewContainerRef: ViewContainerRef,
    private situViewClient: SituViewWebApiClient,
    private renderServiceClient: SituRenderServiceWebApiClient,
    private situReelService: SituReelService) {}

  ngOnInit(): void {
    this.paramSubscription = this.route.paramMap.subscribe(paramMap => {
      const idParam = paramMap.get('id');
      const toParam = paramMap.get('to');
      if (!idParam) {
        return;
      }
      this.showTransition();
      this.resolveAndLoad(idParam, toParam);
    });
  }

  ngOnDestroy(): void {
    this.paramSubscription?.unsubscribe();
    if (this.transitionHideTimer) clearTimeout(this.transitionHideTimer);
  }

  private showTransition(): void {
    if (this.transitionHideTimer) {
      clearTimeout(this.transitionHideTimer);
      this.transitionHideTimer = undefined;
    }
    this.isTransitioning = true;
  }

  private hideTransitionSoon(): void {
    if (this.transitionHideTimer) clearTimeout(this.transitionHideTimer);
    // small grace so the new component has a frame to render its own loading
    // UX underneath before we lift the overlay
    this.transitionHideTimer = setTimeout(() => {
      this.isTransitioning = false;
      this.transitionHideTimer = undefined;
    }, 350);
  }

  private async resolveAndLoad(idParam: string, toParam: string | null): Promise<void> {
    let reel: SituReel | null = null;
    let homeArtifactId = idParam;

    if (toParam) {
      reel = await this.situReelService.getReel(toParam);
      if (reel) {
        homeArtifactId = idParam;
      }
    } else {
      const maybeReel = await this.situReelService.getReel(idParam);
      if (maybeReel) {
        reel = maybeReel;
        homeArtifactId = maybeReel.defaultArtifactId;
      }
    }

    this.activeReel = reel;
    this.selectedArtifactId = reel ? homeArtifactId : undefined;

    if (reel) {
      const params: Params = { ...this.route.snapshot.queryParams, ...this.route.snapshot.params, id: homeArtifactId };
      delete (params as any)['to'];
      const targetItem = reel.items.find(i => i.artifactId === homeArtifactId);
      const startView = this.parseStartViewQuery(targetItem?.startViewQuery);
      if (startView) {
        // Saved per-item override wins over whatever's on the URL
        Object.assign(params, startView);
      }
      this.determineTypeAndAssign(homeArtifactId, params);
    } else {
      this.determineTypeAndAssign(idParam, this.route.snapshot.params);
    }
  }

  onItemSelected(item: SituReelItem): void {
    if (!this.activeReel || !item?.artifactId) {
      return;
    }

    const queryParams: { [k: string]: string | number } = {};
    const override = this.parseStartViewQuery(item.startViewQuery);
    if (override) {
      // Target item has a curator-saved starting view — that wins over the
      // viewer's current angle on cross-item navigation.
      Object.assign(queryParams, override);
    } else if (this.currentComponentRef?.instance instanceof SituViewReadonlyComponent) {
      // No override — preserve the user's current panorama/look angles so they
      // land in roughly the same room in the next reel item.
      const v = this.currentComponentRef.instance as SituViewReadonlyComponent;
      if (v.selectedPanorama) {
        queryParams['panorama'] = v.selectedPanorama;
        if (v.viewingAngle !== undefined) {
          queryParams['hlookat'] = v.viewingAngle;
        }
        if (v.verticalViewingAngle !== undefined) {
          queryParams['vlookat'] = v.verticalViewingAngle;
        }
      }
    }

    this.router.navigate(['/', item.artifactId, this.activeReel.id], { queryParams });
  }

  private parseStartViewQuery(query: string | undefined): { [k: string]: string } | null {
    if (!query) return null;
    try {
      const sp = new URLSearchParams(query.replace(/^\?/, ''));
      const panorama = sp.get('panorama');
      if (!panorama) return null;
      const out: { [k: string]: string } = { panorama };
      const h = sp.get('hlookat');
      const v = sp.get('vlookat');
      if (h !== null) out['hlookat'] = h;
      if (v !== null) out['vlookat'] = v;
      return out;
    } catch {
      return null;
    }
  }

  private loadComponent(component: any, params: any) {
    // SituView only: tearing down and recreating mid-navigation leaves the
    // krpano embed (id "viewer-readonly-pano") behind, and the next instance
    // throws "there is already a html element with this id". Reuse the
    // existing instance and push new params through ngOnChanges instead.
    if (
      component === SituViewReadonlyComponent &&
      this.currentComponentRef &&
      this.currentComponentRef.componentType === component
    ) {
      const instance: any = this.currentComponentRef.instance;
      const previous = instance.routeParams;
      instance.routeParams = params;
      instance.ngOnChanges({ routeParams: new SimpleChange(previous, params, false) });
      this.hideTransitionSoon();
      return;
    }

    this.dynamicContainer.clear();
    this.currentComponentRef = null;

    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = this.dynamicContainer.createComponent(componentFactory);

    const instance: any = componentRef.instance;
    instance.routeParams = params;
    this.currentComponentRef = componentRef;

    this.hideTransitionSoon();
  }

  determineTypeAndAssign(renderedOutputId: string, params: Params) {
    this.getRenderedOutputById(renderedOutputId)
      .then((getFacadeResponse: GetSituFacadeResponse) => {
        if (getFacadeResponse?.success && getFacadeResponse?.artifactId && getFacadeResponse.artifactId !== Guid.EMPTY) {
          params = { ...params, id: getFacadeResponse.artifactId };
          if (isDaydreamProduct(getFacadeResponse.productName) && getFacadeResponse.status !== RenderedOutputStatus.Complete) {
            // An unfinished or failed Daydream has no image, so the link is unavailable
            this.hideTransitionSoon();
            return;
          }
          if (getFacadeResponse.productName === ProductName.Facade || isDaydreamProduct(getFacadeResponse.productName)) {
            // A Daydream is an AI-enhanced facade: same facade.jpg, same viewer.
            this.loadComponent(SituFacadeReadonlyComponent, params);
          } else if (getFacadeResponse.productName === ProductName.Plan) {
            this.loadComponent(SituPlanReadonlyComponent, params);
          } else if (getFacadeResponse.productName === ProductName.Render) {
            this.loadComponent(SituRenderReadonlyComponent, params);
          } else {
            this.loadComponent(SituViewReadonlyComponent, params);
          }
        } else {
          this.renderServiceClient.getSituConnectType(renderedOutputId)
            .then((connectType: string | undefined) => {
              const permittedConnectTypes = ["draftsitulinkpanoramas", "situlinkpanoramas", "hdsitulinkpanoramas", "draftfacade", "facade", "hdfacade"]
              if (!connectType || !permittedConnectTypes.includes(connectType.toLowerCase())) {
                this.getHomeById(renderedOutputId)
                  .then((response: GetHomeResponse) => {
                    if (response.success && response.files?.find(x => x.fileName?.toLowerCase() === "product.glb")) {
                      this.loadComponent(SituProductReadonlyComponent, params);
                    } else {
                      this.loadComponent(SituViewReadonlyComponent, params);
                    }
                  })
                  .catch((err: any) => {
                    this.loadComponent(SituViewReadonlyComponent, params);
                  });
              } else {
                switch(connectType.toLowerCase()) {
                  case "draftsitulinkpanoramas":
                  case "situlinkpanoramas":
                  case "hdsitulinkpanoramas":
                    this.loadComponent(SituViewReadonlyComponent, params);
                    break;
                  case "draftfacade":
                  case "facade":
                  case "hdfacade":
                    this.loadComponent(SituFacadeReadonlyComponent, params);
                }
              }
            })
            .catch(() => this.hideTransitionSoon());
        }
      })
      .catch(() => this.hideTransitionSoon());
  }

  private getRenderedOutputById(renderedOutputId: string): Promise<GetSituFacadeResponse> {
    let request = new GetSituFacadeRequest();
    request.renderedOutputId = renderedOutputId;
    return this.situViewClient.getSituFacade(request);
  }

  private getHomeById(artifactId: string) {
    let request = new GetHomeRequest();
    request.homeId = artifactId;

    return this.situViewClient.getHome(request);
  }
}
